// THE CONSOLE DATA BOUNDARY.
//
// Every console page talks ONLY to a `ConsoleDataSource`. Live traffic uses
// HttpConsoleDataSource → GET /v1/admin/{company}/* (fetch-guard stays green).
// MockConsoleDataSource remains for shell/UI tests (empty/error/loading).

import { ADMIN_FETCH_CREDENTIALS, adminRequestHeaders } from "../admin_fetch.js";
import { isAdminApiPath } from "../admin_path.js";
import { centsToDecimalString, parseDecimal } from "../../../financials-component/src/decimal.js";
import {
  DEFAULT_COMPANY,
  DEFAULT_PERIOD,
  emptyActivitySearch,
  emptyLedgerSearch,
  emptyMonths,
  ledgerReadRefusal,
  LEDGER_VIEW_OFF_SLUG,
  syntheticActivityPage,
  syntheticActivitySearch,
  syntheticLedgerAccounts,
  syntheticLedgerPage,
  syntheticLedgerSearch,
  syntheticLedgerStatement,
  syntheticMonths,
  SYNTHETIC_BALANCE_HISTORY,
  SYNTHETIC_BALANCES,
  SYNTHETIC_BALANCE_SHEET,
  SYNTHETIC_BY_LOCATION,
  SYNTHETIC_CASH_FLOW,
  SYNTHETIC_GL_ENTRIES,
  SYNTHETIC_PNL,
  SYNTHETIC_PY_COMPARISON,
  SYNTHETIC_TRANSACTIONS,
} from "./fixtures.js";
// Zod-free G4 helpers. Do not import `@gv/contracts` here — that pulls the
// schema graph; portal runtime deps stay chart.js-only (GRANT 17 pattern).
import {
  applyGeneralLedgerRunningBalances,
  resolveRunningBalanceModeQuery,
} from "../../../packages/contracts/src/running-balance-mode";
import type { CompanySlug, AccountPath } from "../types.js";
import type {
  ActivityItem,
  ActivityPage,
  ActivityQuery,
  ActivitySearchQuery,
  ActivitySearchResponse,
  AdminMonthsResponse,
  BalanceHistory,
  BalancesSnapshot,
  ConsoleTransactionsPage,
  GeneralLedgerEntry,
  GeneralLedgerPage,
  GeneralLedgerQuery,
  LedgerAccount,
  LedgerAccountsResponse,
  LedgerEntriesPage,
  LedgerEntry,
  LedgerNotPublishedReason,
  LedgerQuery,
  LedgerSearchQuery,
  LedgerSearchResponse,
  LedgerStatement,
  LedgerStatementReport,
  ReportDataMap,
  ReportId,
  ReportSnapshot,
  RunningBalanceMode,
  TransactionsQuery,
} from "./types.js";
import { LEDGER_NOT_PUBLISHED_REASONS } from "./types.js";

export class ConsoleDataError extends Error {
  readonly code?: LedgerNotPublishedReason;

  constructor(
    readonly status: number,
    message = "Console data request failed",
    code?: LedgerNotPublishedReason,
  ) {
    super(message);
    this.name = "ConsoleDataError";
    if (code !== undefined) this.code = code;
  }
}

function isLedgerNotPublishedReason(value: unknown): value is LedgerNotPublishedReason {
  return (
    typeof value === "string" &&
    (LEDGER_NOT_PUBLISHED_REASONS as readonly string[]).includes(value)
  );
}

/** Reads error.details.reason. Body text is never copied into the message. */
export function reasonFromErrorBody(body: unknown): LedgerNotPublishedReason | undefined {
  if (typeof body !== "object" || body === null) return undefined;
  if (!("error" in body)) return undefined;
  const error = body.error;
  if (typeof error !== "object" || error === null) return undefined;
  if (!("details" in error)) return undefined;
  const details = error.details;
  if (typeof details !== "object" || details === null) return undefined;
  if (!("reason" in details)) return undefined;
  return isLedgerNotPublishedReason(details.reason) ? details.reason : undefined;
}

/**
 * The not-published reason when `err` is a 404 carrying one of the three
 * ledger-view codes. A getReport 404 is `not_found` with no details, so this
 * returns null for it — callers of getReport treat any 404 as not published.
 */
export function notPublishedReason(err: unknown): LedgerNotPublishedReason | null {
  if (!(err instanceof ConsoleDataError)) return null;
  if (err.status !== 404) return null;
  if (err.code === undefined) return null;
  return err.code;
}

export function isNotPublished(err: unknown): boolean {
  return notPublishedReason(err) !== null;
}

/**
 * What a month or report says when its ledger copy has not been read yet
 * (B0-2). One sentence, one owner: Home, Books and Reports each wrote it, so a
 * copy in one page made another page's duplicate-sentence count move.
 */
export const MONTH_NOT_LOADED_TEXT = "This month has not been loaded yet.";

export interface ConsoleDataSource {
  getReport<K extends ReportId>(
    company: CompanySlug,
    report: K,
    period: string,
  ): Promise<ReportSnapshot<ReportDataMap[K]>>;
  getGeneralLedger(company: CompanySlug, query: GeneralLedgerQuery): Promise<GeneralLedgerPage>;
  listTransactions(company: CompanySlug, query: TransactionsQuery): Promise<ConsoleTransactionsPage>;
  getBalances(company: CompanySlug): Promise<BalancesSnapshot>;
  getBalanceHistory(company: CompanySlug, accountName: string): Promise<BalanceHistory>;
  /**
   * Optional on the interface so an existing partial test double
   * (admin-portal/test/console-shell.test.ts, not this lane's file) still
   * typechecks. HttpConsoleDataSource and MockConsoleDataSource always
   * implement it. AdminClient fails closed when a double omits it.
   */
  listActivity?(company: CompanySlug, query: ActivityQuery): Promise<ActivityPage>;
  getLedger?(company: CompanySlug, query: LedgerQuery): Promise<LedgerEntriesPage>;
  getLedgerAccounts?(company: CompanySlug, period: string): Promise<LedgerAccountsResponse>;
  getLedgerStatement?(
    company: CompanySlug,
    report: LedgerStatementReport,
    period: string,
  ): Promise<LedgerStatement>;
  /** G8. Optional for existing partial doubles; AdminClient fails closed. */
  getMonths?(company: CompanySlug): Promise<AdminMonthsResponse>;
  /** G9. Optional for existing partial doubles; AdminClient fails closed. */
  searchActivity?(
    company: CompanySlug,
    query: ActivitySearchQuery,
  ): Promise<ActivitySearchResponse>;
  /** G9. Optional for existing partial doubles; AdminClient fails closed. */
  searchLedger?(company: CompanySlug, query: LedgerSearchQuery): Promise<LedgerSearchResponse>;
}

export interface MockConsoleDataSourceOptions {
  /** Return empty-but-valid data from every method (empty state UI proof). */
  empty?: boolean;
  /** Throw this error from every method (error state UI proof). */
  failWith?: Error;
  /** Await this many ms before resolving/rejecting (loading state UI proof). */
  delayMs?: number;
}

const REPORT_LABEL: Record<ReportId, string> = {
  pnl: "pnl",
  "balance-sheet": "balance-sheet",
  "cash-flow": "cash-flow",
  "by-location": "by-location",
  "py-comparison": "py-comparison",
};

const EMPTY_REPORT_DATA: { [K in ReportId]: ReportDataMap[K] } = {
  pnl: {
    currency: "USD",
    periodStart: "",
    periodEnd: "",
    sections: [],
    netIncome: "0.00",
    priorYearNetIncome: null,
  },
  "balance-sheet": {
    currency: "USD",
    asOf: "",
    sections: [],
  },
  "cash-flow": {
    currency: "USD",
    periodStart: "",
    periodEnd: "",
    openingCash: "0.00",
    closingCash: "0.00",
    sections: [],
    netChange: "0.00",
  },
  "by-location": {
    currency: "USD",
    locations: [],
  },
  "py-comparison": {
    currency: "USD",
    priorPeriod: "",
    available: false,
    note: "No comparison data for this period.",
    lines: [],
  },
};

function emptyReportData<K extends ReportId>(report: K): ReportDataMap[K] {
  return EMPTY_REPORT_DATA[report];
}

export function resolveConsoleRunningBalanceMode(input: {
  requested?: string | null | undefined;
  account?: AccountPath | null | undefined;
  location?: string | null | undefined;
}): RunningBalanceMode {
  const resolved = resolveRunningBalanceModeQuery({
    requested: input.requested,
    account: Boolean(input.account),
    location: Boolean(input.location),
  });
  if (!resolved.ok) {
    throw new ConsoleDataError(400, "Invalid runningBalanceMode");
  }
  return resolved.mode;
}

export function paginateGeneralLedger(input: {
  company: CompanySlug;
  period: string;
  entries: readonly GeneralLedgerEntry[];
  account?: AccountPath | null | undefined;
  location?: string | null | undefined;
  runningBalanceMode?: string | null | undefined;
  page: number;
  pageSize: number;
}): GeneralLedgerPage {
  const filtered = input.entries.filter((entry) => {
    if (
      input.account &&
      !wirePath(entry.account)
        .toLowerCase()
        .includes(wirePath(input.account).toLowerCase())
    ) {
      return false;
    }
    if (input.location && entry.location !== input.location) return false;
    return true;
  });
  const runningBalanceMode = resolveConsoleRunningBalanceMode({
    requested: input.runningBalanceMode,
    account: input.account,
    location: input.location,
  });
  const withBalances = applyGeneralLedgerRunningBalances(
    filtered,
    runningBalanceMode,
  );
  const start = (input.page - 1) * input.pageSize;
  return {
    company: input.company,
    period: input.period,
    entries: withBalances.slice(start, start + input.pageSize),
    page: input.page,
    pageSize: input.pageSize,
    total: filtered.length,
    runningBalanceMode,
  };
}

/** Mock implementation of ConsoleDataSource, backed by synthetic fixtures. */
export class MockConsoleDataSource implements ConsoleDataSource {
  private readonly options: MockConsoleDataSourceOptions;

  constructor(options: MockConsoleDataSourceOptions = {}) {
    this.options = options;
  }

  private async settle<T>(build: () => T): Promise<T> {
    if (this.options.delayMs) {
      await new Promise((resolve) => setTimeout(resolve, this.options.delayMs));
    }
    if (this.options.failWith) throw this.options.failWith;
    return build();
  }

  getReport<K extends ReportId>(
    company: CompanySlug,
    report: K,
    period: string,
  ): Promise<ReportSnapshot<ReportDataMap[K]>> {
    return this.settle(() => {
      if (this.options.empty) {
        return {
          apiVersion: "v1",
          company,
          report: REPORT_LABEL[report],
          period,
          generatedAt: new Date(0).toISOString(),
          pipelineRunId: "syn-run-empty",
          data: emptyReportData(report),
        };
      }
      const source = REPORT_SOURCE[report];
      return { ...source, company, period };
    });
  }

  getGeneralLedger(company: CompanySlug, query: GeneralLedgerQuery): Promise<GeneralLedgerPage> {
    return this.settle(() => {
      const period = query.period ?? DEFAULT_PERIOD;
      const page = query.page ?? 1;
      const pageSize = query.pageSize ?? 20;
      if (this.options.empty) {
        return {
          company,
          period,
          entries: [],
          page,
          pageSize,
          total: 0,
          runningBalanceMode: resolveConsoleRunningBalanceMode({
            requested: query.runningBalanceMode,
            account: query.account,
            location: query.location,
          }),
        };
      }
      const inPeriod = SYNTHETIC_GL_ENTRIES.filter((entry) => entry.date.startsWith(period));
      return paginateGeneralLedger({
        company,
        period,
        entries: inPeriod,
        account: query.account,
        location: query.location,
        runningBalanceMode: query.runningBalanceMode,
        page,
        pageSize,
      });
    });
  }

  listTransactions(
    company: CompanySlug,
    query: TransactionsQuery,
  ): Promise<ConsoleTransactionsPage> {
    return this.settle(() => {
      const page = query.page ?? 1;
      const pageSize = query.pageSize ?? 20;
      if (this.options.empty) {
        return { company, transactions: [], page, pageSize, total: 0, has_more: false };
      }
      const period = query.period;
      const filtered = SYNTHETIC_TRANSACTIONS.filter((txn) => {
        if (period && !txn.date.startsWith(period)) return false;
        if (query.status && txn.status !== query.status) return false;
        return true;
      }).slice()
        .sort((a, b) => {
          if (a.date !== b.date) return a.date < b.date ? 1 : -1;
          return a.id < b.id ? 1 : a.id > b.id ? -1 : 0;
        });
      let startIdx = 0;
      if (query.afterDate && query.afterId) {
        const afterDate = query.afterDate;
        const afterId = query.afterId;
        const idx = filtered.findIndex(
          (txn) =>
            txn.date < afterDate
            || (txn.date === afterDate && txn.id < afterId),
        );
        startIdx = idx === -1 ? filtered.length : idx;
      } else if (page > 1) {
        startIdx = (page - 1) * pageSize;
      }
      const transactions = filtered.slice(startIdx, startIdx + pageSize);
      return {
        company,
        transactions,
        page,
        pageSize,
        total: filtered.length,
        has_more: startIdx + transactions.length < filtered.length,
      };
    });
  }

  getBalances(company: CompanySlug): Promise<BalancesSnapshot> {
    return this.settle(() => {
      if (this.options.empty) {
        return { company, asOf: null, accounts: [] };
      }
      return { ...SYNTHETIC_BALANCES, company };
    });
  }

  getBalanceHistory(company: CompanySlug, accountName: string): Promise<BalanceHistory> {
    return this.settle(() => {
      if (this.options.empty) {
        return { company, accountName, points: [] };
      }
      const history = SYNTHETIC_BALANCE_HISTORY[accountName];
      return { company, accountName, points: history?.points ?? [] };
    });
  }

  listActivity(company: CompanySlug, query: ActivityQuery): Promise<ActivityPage> {
    return this.settle(() => {
      const page = query.page ?? 1;
      const pageSize = query.pageSize ?? 50;
      if (this.options.empty) {
        return {
          items: [],
          page,
          pageSize,
          total: 0,
          hasMore: false,
          sources: {
            bank: {},
            moov: { walletConfigured: false },
            ledger: { available: false },
          },
        };
      }
      return paginateActivity(
        syntheticActivityPage(wireSlug(company), monthOf(query)),
        query,
      );
    });
  }

  getLedger(company: CompanySlug, query: LedgerQuery): Promise<LedgerEntriesPage> {
    return this.settle(() => {
      const page = query.page ?? 1;
      const pageSize = query.pageSize ?? 50;
      const month = monthOf(query);
      if (this.options.empty) {
        return {
          header: emptyLedgerHeader(company, month),
          entries: [],
          page,
          pageSize,
          total: 0,
          hasMore: false,
        };
      }
      this.throwIfUnpublished(company, month, "entries");
      return paginateLedger(syntheticLedgerPage(wireSlug(company), month), query);
    });
  }

  getLedgerAccounts(company: CompanySlug, period: string): Promise<LedgerAccountsResponse> {
    return this.settle(() => {
      if (this.options.empty) {
        return {
          header: emptyLedgerHeader(company, period),
          accounts: [],
          trialBalance: { totalDebits: "0.00", totalCredits: "0.00" },
        };
      }
      this.throwIfUnpublished(company, period, "accounts");
      return syntheticLedgerAccounts(wireSlug(company), period);
    });
  }

  getLedgerStatement(
    company: CompanySlug,
    report: LedgerStatementReport,
    period: string,
  ): Promise<LedgerStatement> {
    return this.settle(() => {
      if (this.options.empty) {
        return {
          apiVersion: "v1",
          company,
          report,
          period,
          generatedAt: new Date(0).toISOString(),
          pipelineRunId: "syn-run-empty",
          data: { currency: "USD" },
          state: "open",
          asOf: new Date(0).toISOString(),
          throughDate: "2026-01-01",
        };
      }
      this.throwIfUnpublished(company, period, "statement");
      return syntheticLedgerStatement(wireSlug(company), report, period);
    });
  }

  getMonths(company: CompanySlug): Promise<AdminMonthsResponse> {
    return this.settle(() =>
      this.options.empty ? emptyMonths(wireSlug(company)) : syntheticMonths(wireSlug(company)),
    );
  }

  searchActivity(
    company: CompanySlug,
    query: ActivitySearchQuery,
  ): Promise<ActivitySearchResponse> {
    return this.settle(() =>
      this.options.empty
        ? emptyActivitySearch()
        : syntheticActivitySearch(wireSlug(company), query),
    );
  }

  searchLedger(company: CompanySlug, query: LedgerSearchQuery): Promise<LedgerSearchResponse> {
    return this.settle(() => {
      if (this.options.empty) return emptyLedgerSearch(wireSlug(company));
      if (company === LEDGER_VIEW_OFF_SLUG) {
        throw new ConsoleDataError(404, "Console data request failed", "ledger_view_not_enabled");
      }
      return syntheticLedgerSearch(wireSlug(company), query);
    });
  }

  private throwIfUnpublished(
    company: CompanySlug,
    period: string,
    surface: "entries" | "accounts" | "statement",
  ): void {
    const reason = ledgerReadRefusal(wireSlug(company), period, surface);
    if (reason === null) return;
    throw new ConsoleDataError(404, "Console data request failed", reason);
  }
}

const REPORT_SOURCE: { [K in ReportId]: ReportSnapshot<ReportDataMap[K]> } = {
  pnl: SYNTHETIC_PNL,
  "balance-sheet": SYNTHETIC_BALANCE_SHEET,
  "cash-flow": SYNTHETIC_CASH_FLOW,
  "by-location": SYNTHETIC_BY_LOCATION,
  "py-comparison": SYNTHETIC_PY_COMPARISON,
};

export function createMockConsoleDataSource(
  options?: MockConsoleDataSourceOptions,
): ConsoleDataSource {
  return new MockConsoleDataSource(options);
}

/**
 * Live ConsoleDataSource — hits admin-scoped read endpoints only
 * (`/v1/admin/{company}/*`). Same-origin credentials so Access sees the
 * session cookie; bearer is omitted when the Pages Function injects it.
 * Never calls entity/hub paths.
 */
export class HttpConsoleDataSource implements ConsoleDataSource {
  private readonly apiBase: string;
  private readonly token: string;

  constructor(apiBase: string, token: string) {
    this.apiBase = apiBase.replace(/\/+$/, "");
    this.token = token;
  }

  getReport<K extends ReportId>(
    company: CompanySlug,
    report: K,
    period: string,
  ): Promise<ReportSnapshot<ReportDataMap[K]>> {
    // getAdminReport's 404 is `not_found` with no details.reason. isNotPublished
    // stays false. Callers treat any 404 from this method as not published.
    const qs = new URLSearchParams({ period });
    return this.request(
      `/v1/admin/${encodeURIComponent(wireSlug(company))}/reports/${encodeURIComponent(report)}?${qs}`,
    );
  }

  getGeneralLedger(company: CompanySlug, query: GeneralLedgerQuery): Promise<GeneralLedgerPage> {
    const qs = new URLSearchParams();
    if (query.period) qs.set("period", query.period);
    if (query.account) qs.set("account", query.account);
    if (query.location) qs.set("location", query.location);
    if (query.page !== undefined) qs.set("page", String(query.page));
    if (query.pageSize !== undefined) qs.set("pageSize", String(query.pageSize));
    if (
      query.runningBalanceMode === "company" ||
      query.runningBalanceMode === "filtered"
    ) {
      qs.set("runningBalanceMode", query.runningBalanceMode);
    }
    const suffix = qs.toString() ? `?${qs}` : "";
    return this.request(
      `/v1/admin/${encodeURIComponent(wireSlug(company))}/general-ledger${suffix}`,
    );
  }

  listTransactions(
    company: CompanySlug,
    query: TransactionsQuery,
  ): Promise<ConsoleTransactionsPage> {
    const qs = new URLSearchParams();
    if (query.period) qs.set("period", query.period);
    if (query.status) qs.set("status", query.status);
    if (query.page !== undefined) qs.set("page", String(query.page));
    if (query.pageSize !== undefined) qs.set("pageSize", String(query.pageSize));
    if (query.afterDate) qs.set("after_date", query.afterDate);
    if (query.afterId) qs.set("after_id", query.afterId);
    const suffix = qs.toString() ? `?${qs}` : "";
    return this.request(
      `/v1/admin/${encodeURIComponent(wireSlug(company))}/transactions${suffix}`,
    );
  }

  getBalances(company: CompanySlug): Promise<BalancesSnapshot> {
    return this.request(`/v1/admin/${encodeURIComponent(wireSlug(company))}/balances`);
  }

  getBalanceHistory(company: CompanySlug, accountName: string): Promise<BalanceHistory> {
    const qs = new URLSearchParams({ account: accountName });
    return this.request(
      `/v1/admin/${encodeURIComponent(wireSlug(company))}/balances/history?${qs}`,
    );
  }

  listActivity(company: CompanySlug, query: ActivityQuery): Promise<ActivityPage> {
    // rd15 A4: a month, or a range. The two are never sent together.
    const qs = new URLSearchParams();
    setQuery(qs, "period", query.period);
    setQuery(qs, "from", query.from);
    setQuery(qs, "to", query.to);
    setQuery(qs, "account", query.account);
    setQuery(qs, "minAmount", query.minAmount);
    setQuery(qs, "maxAmount", query.maxAmount);
    setQuery(qs, "direction", query.direction);
    setQuery(qs, "q", query.q);
    setQuery(qs, "source", query.source);
    setQuery(qs, "state", query.state);
    setQuery(qs, "page", query.page);
    setQuery(qs, "pageSize", query.pageSize);
    return this.request(
      `/v1/admin/${encodeURIComponent(wireSlug(company))}/activity?${qs}`,
    );
  }

  getLedger(company: CompanySlug, query: LedgerQuery): Promise<LedgerEntriesPage> {
    const qs = new URLSearchParams();
    setQuery(qs, "period", query.period);
    setQuery(qs, "q", query.q);
    setQuery(qs, "account", ledgerAccountKey(query));
    setQuery(qs, "accountPrefix", wirePathOrUndefined(query.accountPrefix));
    setQuery(qs, "location", query.location);
    setQuery(qs, "lease", query.lease);
    setQuery(qs, "source", query.source);
    setQuery(qs, "from", query.from);
    setQuery(qs, "to", query.to);
    setQuery(qs, "page", query.page);
    setQuery(qs, "pageSize", query.pageSize);
    return this.request(`/v1/admin/${encodeURIComponent(wireSlug(company))}/ledger?${qs}`);
  }

  getLedgerAccounts(company: CompanySlug, period: string): Promise<LedgerAccountsResponse> {
    const qs = new URLSearchParams({ period });
    return this.request(
      `/v1/admin/${encodeURIComponent(wireSlug(company))}/ledger/accounts?${qs}`,
    );
  }

  getLedgerStatement(
    company: CompanySlug,
    report: LedgerStatementReport,
    period: string,
  ): Promise<LedgerStatement> {
    const qs = new URLSearchParams({ period });
    return this.request(
      `/v1/admin/${encodeURIComponent(wireSlug(company))}/ledger/statements/${encodeURIComponent(report)}?${qs}`,
    );
  }

  getMonths(company: CompanySlug): Promise<AdminMonthsResponse> {
    return this.request(`/v1/admin/${encodeURIComponent(wireSlug(company))}/months`);
  }

  searchActivity(
    company: CompanySlug,
    query: ActivitySearchQuery,
  ): Promise<ActivitySearchResponse> {
    const qs = new URLSearchParams({ q: query.q });
    setQuery(qs, "source", query.source);
    setQuery(qs, "state", query.state);
    return this.request(
      `/v1/admin/${encodeURIComponent(wireSlug(company))}/activity/search?${qs}`,
    );
  }

  searchLedger(company: CompanySlug, query: LedgerSearchQuery): Promise<LedgerSearchResponse> {
    const qs = new URLSearchParams();
    setQuery(qs, "q", query.q);
    setQuery(qs, "account", ledgerAccountKey(query));
    setQuery(qs, "accountPrefix", wirePathOrUndefined(query.accountPrefix));
    setQuery(qs, "location", query.location);
    setQuery(qs, "lease", query.lease);
    setQuery(qs, "source", query.source);
    const suffix = qs.toString() ? `?${qs}` : "";
    return this.request(`/v1/admin/${encodeURIComponent(wireSlug(company))}/ledger/search${suffix}`);
  }

  private async request<T>(path: string): Promise<T> {
    if (!isAdminApiPath(path)) {
      throw new Error(`HttpConsoleDataSource: refusing non-admin path ${path}`);
    }
    const response = await fetch(`${this.apiBase}${path}`, {
      method: "GET",
      credentials: ADMIN_FETCH_CREDENTIALS,
      headers: adminRequestHeaders(this.token, {
        Accept: "application/json",
      }),
    });
    if (!response.ok) {
      const body: unknown = await response.json().catch(() => null);
      const code = reasonFromErrorBody(body);
      if (code !== undefined) {
        throw new ConsoleDataError(response.status, "Console data request failed", code);
      }
      throw new ConsoleDataError(response.status, "Console data request failed");
    }
    return (await response.json()) as T;
  }
}

function setQuery(qs: URLSearchParams, key: string, value: string | number | undefined): void {
  if (value === undefined) return;
  qs.set(key, String(value));
}

/**
 * The wire boundary for a branded key. Inside this data boundary the slug and
 * the account path are request/response data, never words; converting to the
 * primitive is the explicit act (the brand guard lets this through where a
 * cast or an interpolation would be a leak).
 */
function wireSlug(slug: CompanySlug): string {
  return slug.valueOf();
}

function wirePath(path: AccountPath): string {
  return path.valueOf();
}

/** The same boundary, for an optional query field. */
/**
 * The month a fixture answers for. A range names none, so the fixture uses the
 * month the range ends in — the same month the Worker's header names.
 */
function monthOf(query: { period?: string; from?: string; to?: string }): string {
  return query.period ?? query.to?.slice(0, 7) ?? query.from?.slice(0, 7) ?? "";
}

/** One account, or several: the route takes either, comma separated. */
function ledgerAccountKey(query: LedgerQuery): string | undefined {
  if (query.accounts !== undefined && query.accounts.length > 0) {
    return query.accounts.join(",");
  }
  return wirePathOrUndefined(query.account);
}

function wirePathOrUndefined(path: AccountPath | null | undefined): string | undefined {
  return path === null || path === undefined ? undefined : path.valueOf();
}

function emptyLedgerHeader(company: CompanySlug, period: string): LedgerEntriesPage["header"] {
  return {
    company,
    period,
    state: "open",
    asOf: new Date(0).toISOString(),
    ledgerCommitAt: new Date(0).toISOString(),
    loaderErrors: 0,
    waitingForReview: 0,
    periods: [],
  };
}

function includesFold(haystack: string | undefined, needle: string): boolean {
  if (haystack === undefined) return false;
  return haystack.toLowerCase().includes(needle);
}

/** A magnitude, for an amount window: the sign says which way, not how big. */
function absoluteCents(cents: bigint): bigint {
  return cents < 0n ? -cents : cents;
}

function activityMatches(item: ActivityItem, query: ActivityQuery): boolean {
  if (query.source !== undefined && query.source !== "all" && item.source !== query.source) {
    return false;
  }
  if (query.state !== undefined && item.state !== query.state) return false;
  // rd15 A4: the same bounds the Worker applies, so the fixture answers like
  // the real service. Fix round 1: a magnitude and a direction, never a signed
  // comparison (that hid every charge).
  if (query.account !== undefined && item.account?.id !== query.account) return false;
  if (query.from !== undefined && item.date < query.from) return false;
  if (query.to !== undefined && item.date > query.to) return false;
  if (query.minAmount !== undefined && parseDecimal(query.minAmount) > absoluteCents(parseDecimal(item.amount))) {
    return false;
  }
  if (query.maxAmount !== undefined && parseDecimal(query.maxAmount) < absoluteCents(parseDecimal(item.amount))) {
    return false;
  }
  if (query.direction !== undefined && query.direction !== "both") {
    const amount = parseDecimal(item.amount);
    if (query.direction === "in" ? amount <= 0 : amount >= 0) return false;
  }
  const q = query.q?.trim();
  if (q === undefined || q.length === 0) return true;
  const folded = q.toLowerCase();
  if (includesFold(item.description, folded)) return true;
  if (includesFold(item.amount, folded)) return true;
  if (includesFold(item.fee, folded)) return true;
  if (includesFold(item.net, folded)) return true;
  if (includesFold(item.refId, folded)) return true;
  if (includesFold(item.moov?.property, folded)) return true;
  if (includesFold(item.moov?.lease, folded)) return true;
  return false;
}

function paginateActivity(source: ActivityPage, query: ActivityQuery): ActivityPage {
  const page = query.page ?? 1;
  const pageSize = query.pageSize ?? source.pageSize;
  const matched = source.items.filter((item) => activityMatches(item, query));
  const start = (page - 1) * pageSize;
  const items = matched.slice(start, start + pageSize);
  return {
    items,
    page,
    pageSize,
    total: matched.length,
    hasMore: start + items.length < matched.length,
    sources: source.sources,
  };
}

function entryText(entry: LedgerEntry): string[] {
  const values = [
    entry.payee,
    entry.narration,
    entry.location,
    entry.ids.lease,
    entry.ids.plaidId,
    entry.ids.moovId,
    entry.ids.pairedPlaidId,
    entry.view.cashDelta,
  ];
  for (const posting of entry.postings) {
    values.push(wirePath(posting.account), posting.amount, posting.location);
  }
  return values.filter((value): value is string => value !== undefined);
}

function ledgerEntryMatches(entry: LedgerEntry, query: LedgerQuery): boolean {
  if (query.account !== undefined) {
    const account = query.account;
    if (!entry.postings.some((posting) => posting.account === account)) return false;
  }
  if (query.accountPrefix !== undefined) {
    const prefix = query.accountPrefix;
    const boundary = `${wirePath(prefix)}:`;
    const hit = entry.postings.some(
      (posting) =>
        posting.account === prefix || wirePath(posting.account).startsWith(boundary),
    );
    if (!hit) return false;
  }
  if (query.location !== undefined && entry.location !== query.location) return false;
  if (query.lease !== undefined && entry.ids.lease !== query.lease) return false;
  if (query.source !== undefined && entry.source !== query.source) return false;
  if (query.from !== undefined && entry.date < query.from) return false;
  if (query.to !== undefined && entry.date > query.to) return false;
  const q = query.q?.trim();
  if (q === undefined || q.length === 0) return true;
  const folded = q.toLowerCase();
  return entryText(entry).some((value) => value.toLowerCase().includes(folded));
}

function withAccountRunning(
  entries: readonly LedgerEntry[],
  account: AccountPath,
  opening: bigint,
): LedgerEntry[] {
  let running = opening;
  const stamped: LedgerEntry[] = [];
  for (const entry of entries) {
    const posting = entry.postings.find((row) => row.account === account);
    if (posting === undefined) continue;
    running += parseDecimal(posting.amount);
    stamped.push({
      ...entry,
      accountAmount: posting.amount,
      balanceAfter: centsToDecimalString(running),
    });
  }
  return stamped;
}

function openingCents(
  accounts: readonly LedgerAccount[],
  account: AccountPath,
): bigint {
  const row = accounts.find((item) => item.account === account);
  if (row === undefined) return 0n;
  return parseDecimal(row.opening);
}

function paginateLedger(source: LedgerEntriesPage, query: LedgerQuery): LedgerEntriesPage {
  const page = query.page ?? 1;
  const pageSize = query.pageSize ?? source.pageSize;
  let matched = source.entries.filter((entry) => ledgerEntryMatches(entry, query));
  if (query.account !== undefined) {
    const accounts = syntheticLedgerAccounts(wireSlug(source.header.company), source.header.period);
    matched = withAccountRunning(
      matched,
      query.account,
      openingCents(accounts.accounts, query.account),
    );
  }
  const start = (page - 1) * pageSize;
  const entries = matched.slice(start, start + pageSize);
  return {
    header: source.header,
    entries,
    page,
    pageSize,
    total: matched.length,
    hasMore: start + entries.length < matched.length,
  };
}

export function createHttpConsoleDataSource(
  apiBase: string,
  token: string,
): ConsoleDataSource {
  return new HttpConsoleDataSource(apiBase, token);
}

// Re-exported so callers of createMockConsoleDataSource() don't need a second
// import just to know the default company/period the mock is seeded with.
export { DEFAULT_COMPANY, DEFAULT_PERIOD };
